'use client';

import React, { useState } from 'react';
import Link from 'next/link';

const OBJECTIONS = [
  {
    category: 'Switching Agents',
    items: [
      {
        q: "I\'m mid-tenancy — can I really switch agents without disrupting my tenant?",
        a: "Yes, and it's more common than you think. We handle the entire switch on your behalf: we notify your current agent, inform your tenant professionally (Section 48 compliant), and transfer the deposit scheme. Your tenant's day-to-day experience is unaffected. Most switches complete within 5–10 working days. If your current agent delays or obstructs, our in-house solicitor steps in — no other Chester agent can offer that.",
      },
      {
        q: "My current agent has a long notice period. Am I stuck?",
        a: "Check your management agreement — most notice periods are 1–3 months, and many have break clauses at tenancy renewal. We'll review your contract for free and advise on the cleanest exit. In some cases, the cost of staying with a poor agent far outweighs any early-exit fee. We can help you weigh that up honestly.",
      },
      {
        q: "What if my current agent refuses to hand over documents?",
        a: "They are legally obliged to hand over all tenancy documentation, deposit details, and compliance certificates. If they delay or refuse, our in-house solicitor can write to them formally — which tends to resolve things quickly. This is a service no other Chester letting agent can offer in-house.",
      },
    ],
  },
  {
    category: 'Void Periods & Costs',
    items: [
      {
        q: "How quickly can you find a tenant? I can\'t afford a long void.",
        a: "Our average void period for managed properties is under 11 days — compared to the industry average of 28–42 days. We list on Rightmove, Zoopla, and OnTheMarket simultaneously, and we have an active applicant database. For full management clients, re-letting is included at no additional let fee when a tenancy ends.",
      },
      {
        q: "Are your fees really transparent? My last agent kept adding charges.",
        a: "Yes. Our fees are fixed and published — no admin fees, no renewal surprises, no contractor mark-ups on works under £250. You can see our full fee schedule at /fixed-fees-landlords before you sign anything. If a charge isn't on that page, we don't charge it.",
      },
      {
        q: "What happens to my management fee if the property is empty?",
        a: "No management fee is charged during void periods for rent collection and full management clients. We only earn our fee when you earn rent. That\'s the right incentive structure — and it\'s why we work hard to minimise voids.",
      },
    ],
  },
  {
    category: 'Contractors & Maintenance',
    items: [
      {
        q: "I\'ve had bad experiences with agents using expensive or unreliable contractors.",
        a: "We use a vetted contractor network built over years of local relationships. We do not mark up works under £250 — you pay the contractor's invoice directly. For works over £250, we obtain two quotes and seek your approval before instructing. You receive a full maintenance log via your landlord portal, so nothing is hidden.",
      },
      {
        q: "Can I use my own contractors?",
        a: "Yes, for full management clients. If you have a trusted plumber, electrician, or builder, we're happy to use them — provided they hold the appropriate insurance and qualifications. We just need their details on file. Your property, your preferences.",
      },
    ],
  },
  {
    category: 'Legal & Compliance',
    items: [
      {
        q: "The Renters Rights Act is confusing. How do I know I'll be compliant?",
        a: "The Renters Rights Act (in force from 1 May 2026) abolishes Section 21, moves all tenancies to periodic, and introduces new Section 8 grounds. We have an in-house solicitor who specialises in residential landlord-tenant law. Every managed property gets a compliance audit at instruction, and we proactively flag changes as legislation evolves. You won't be left guessing.",
      },
      {
        q: "What if I need to evict a tenant? Can you handle that?",
        a: "Yes — entirely in-house. From serving the correct notice to representing you at a possession hearing, our PDA Law solicitor handles every step at fixed fees. You won't be referred to an external firm, and you won't face an unknown bill. Most landlords with other agents only discover this gap when they need it most.",
      },
    ],
  },
  {
    category: 'Unhappy Landlords & Trust',
    items: [
      {
        q: "I've been let down by agents before. Why should I trust PDA?",
        a: "We understand the scepticism — it's earned. The difference is accountability: we have an in-house solicitor, published fixed fees, a real-time landlord portal, and a track record of landlords who've switched to us and stayed. We'd rather you read our reviews and speak to a current client than take our word for it. Ask us to put you in touch with one.",
      },
      {
        q: "What if I'm unhappy with PDA after I've switched?",
        a: "There are no exit fees. If you want to self-manage or move to another agent, we'll facilitate a clean handover — the same professional process we'd expect from any agent. We don't hold landlords hostage. Our retention rate speaks for itself.",
      },
      {
        q: "Do you manage properties outside Chester?",
        a: "Yes. We manage properties across Chester, the Wirral, and North Wales. If your property is in one of these areas, we can help. If you're unsure whether we cover your postcode, call us on 01244 380 380 — we'll tell you honestly.",
      },
    ],
  },
];

interface LandlordObjectionFAQProps {
  className?: string;
  /** Show only specific categories */
  categories?: string[];
  /** Dark or light background */
  theme?: 'light' | 'dark';
}

export default function LandlordObjectionFAQ({
  className = '',
  categories,
  theme = 'light',
}: LandlordObjectionFAQProps) {
  const [openItem, setOpenItem] = useState<string | null>(null);

  const filteredGroups = categories
    ? OBJECTIONS.filter((g) => categories.includes(g.category))
    : OBJECTIONS;

  const bg = theme === 'dark' ? 'bg-[#0F2137]' : 'bg-white';
  const headingColor = theme === 'dark' ? 'text-white' : 'text-[#0F2137]';
  const subColor = theme === 'dark' ? 'text-[#8A9BB0]' : 'text-[#6B7280]';
  const categoryColor = theme === 'dark' ? 'text-[#4DB896]' : 'text-[#1B6B5A]';
  const cardBg = theme === 'dark' ? 'bg-[#0a1e30] border-white/8' : 'bg-white border-[#E0DDD4]';
  const questionColor = theme === 'dark' ? 'text-white' : 'text-[#0F2137]';
  const answerColor = theme === 'dark' ? 'text-[#8A9BB0]' : 'text-[#4A5E74]';
  const hoverBg = theme === 'dark' ? 'hover:bg-white/5' : 'hover:bg-[#F7F6F3]';
  const dividerColor = theme === 'dark' ? 'border-white/8' : 'border-[#E0DDD4]';
  const eyebrowBg = theme === 'dark' ? 'bg-[#1B6B5A]/20 border-[#1B6B5A]/30 text-[#4DB896]' : 'bg-[#1B6B5A]/10 border-[#1B6B5A]/20 text-[#1B6B5A]';

  return (
    <section className={`py-20 md:py-28 ${bg} ${className}`} aria-labelledby="objection-faq-heading">
      <div className="max-w-5xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-14">
          <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold tracking-widest uppercase mb-5 ${eyebrowBg}`}>
            Real Questions, Honest Answers
          </span>
          <h2
            id="objection-faq-heading"
            className={`font-display text-3xl md:text-4xl tracking-tight leading-tight mb-4 ${headingColor}`}
          >
            The questions landlords actually ask —{' '}
            <span className="italic font-light text-[#1B6B5A]">before they enquire.</span>
          </h2>
          <p className={`text-base leading-relaxed max-w-2xl mx-auto ${subColor}`}>
            We&apos;ve answered the objections that stop landlords from making contact. If your question isn&apos;t here, call us on{' '}
            <a href="tel:01244380380" className="text-[#1B6B5A] hover:underline font-medium">01244 380 380</a>.
          </p>
        </div>

        {/* FAQ groups */}
        <div className="space-y-10">
          {filteredGroups.map((group) => (
            <div key={group.category}>
              <h3 className={`text-xs font-bold tracking-widest uppercase mb-4 ${categoryColor}`}>
                {group.category}
              </h3>
              <div className="space-y-2">
                {group.items.map((item, idx) => {
                  const key = `${group.category}-${idx}`;
                  const isOpen = openItem === key;
                  return (
                    <div
                      key={key}
                      className={`rounded-xl border overflow-hidden shadow-sm ${cardBg}`}
                    >
                      <button
                        className={`w-full flex items-center justify-between gap-4 p-5 text-left transition-colors ${hoverBg}`}
                        onClick={() => setOpenItem(isOpen ? null : key)}
                        aria-expanded={isOpen}
                      >
                        <span className={`font-semibold text-sm leading-snug ${questionColor}`}>
                          {item.q}
                        </span>
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#1B6B5A"
                          strokeWidth="2"
                          strokeLinecap="round"
                          className={`flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                          aria-hidden="true"
                        >
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </button>
                      {isOpen && (
                        <div className={`px-5 pb-5 border-t ${dividerColor}`}>
                          <p className={`text-sm leading-relaxed pt-4 ${answerColor}`}>{item.a}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 text-center">
          <p className={`text-sm mb-5 ${subColor}`}>
            Still have a question we haven&apos;t answered?
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              href="/contact?type=consultation"
              className="inline-flex items-center justify-center gap-2 bg-[#1B6B5A] hover:bg-[#155a4a] text-white font-semibold text-sm py-3 px-7 rounded-lg transition-colors duration-200"
            >
              Book a Free 20-Min Consultation
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
              </svg>
            </Link>
            <a
              href="tel:01244380380"
              className={`inline-flex items-center justify-center gap-2 border font-medium text-sm py-3 px-7 rounded-lg transition-colors duration-200 ${
                theme === 'dark' ?'border-white/20 text-white/80 hover:border-white/40 hover:text-white' :'border-[#1B6B5A]/30 text-[#1B6B5A] hover:bg-[#1B6B5A]/5'
              }`}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.18 2 2 0 0 1 3.6 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.6a16 16 0 0 0 6 6l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
              </svg>
              01244 380 380
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
